import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const FEATURES = [
  {
    icon: '🤖',
    title: 'AI question generation from PDF',
    titleBn: 'PDF থেকে AI প্রশ্ন তৈরি',
    desc: 'Upload any PDF and let AI create MCQs with two-pass answer verification.',
    descBn: 'যেকোনো PDF আপলোড করুন, AI দুই-ধাপে যাচাই করে MCQ তৈরি করে দেবে।',
  },
  {
    icon: '⚡',
    title: 'Auto-grading & instant results',
    titleBn: 'অটো-গ্রেডিং ও তাৎক্ষণিক ফলাফল',
    desc: 'Students get results the moment they submit — no manual checking.',
    descBn: 'জমা দেওয়ার সাথে সাথেই ফলাফল, হাতে খাতা দেখার দরকার নেই।',
  },
  {
    icon: '📲',
    title: 'WhatsApp exam sharing',
    titleBn: 'WhatsApp-এ পরীক্ষা শেয়ার',
    desc: 'Share the exam link with your class on WhatsApp in one tap.',
    descBn: 'এক ট্যাপে WhatsApp-এ ক্লাসের সাথে পরীক্ষার লিংক শেয়ার করুন।',
  },
  {
    icon: '🏆',
    title: 'Public leaderboards',
    titleBn: 'পাবলিক লিডারবোর্ড',
    desc: 'Keep students motivated with a shareable rank list after each exam.',
    descBn: 'পরীক্ষার পর শেয়ারযোগ্য র‍্যাঙ্ক লিস্ট দিয়ে ছাত্রছাত্রীদের উৎসাহিত করুন।',
  },
  {
    icon: '📐',
    title: 'Math & Bengali support',
    titleBn: 'গণিত ও বাংলা সাপোর্ট',
    desc: 'Full LaTeX/KaTeX formula rendering plus native Bengali question text.',
    descBn: 'সম্পূর্ণ LaTeX/KaTeX সূত্র এবং স্বাভাবিক বাংলা প্রশ্নের সাপোর্ট।',
  },
  {
    icon: '🗂️',
    title: 'Question bank & reuse',
    titleBn: 'প্রশ্ন ব্যাংক ও পুনর্ব্যবহার',
    desc: 'Save every question once and reuse it across future exams.',
    descBn: 'প্রতিটি প্রশ্ন একবার সেভ করুন, ভবিষ্যতের পরীক্ষাতেও ব্যবহার করুন।',
  },
];

const STEPS = [
  { icon: '📄', title: 'Upload PDF', titleBn: 'PDF আপলোড করুন' },
  { icon: '🤖', title: 'AI generates & verifies questions', titleBn: 'AI প্রশ্ন তৈরি ও যাচাই করে' },
  { icon: '🚀', title: 'Publish exam', titleBn: 'পরীক্ষা পাবলিশ করুন' },
  { icon: '📲', title: 'Share link on WhatsApp', titleBn: 'WhatsApp-এ লিংক শেয়ার করুন' },
];

export default function Landing() {
  const { user, loading, settings } = useAuth();

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (user) return <Navigate to="/dashboard" />;

  const brandName = settings.brand_name || 'Oexams';
  const tagline = settings.tagline || 'Smart Exams for Smart Teachers';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {settings.logo_path
              ? <img src={settings.logo_path} alt="logo" style={{ height: 34, objectFit: 'contain' }} />
              : <span style={{ fontSize: 24 }}>📝</span>}
            <span style={{ fontWeight: 800, fontSize: 19, color: 'var(--primary-light)' }}>{brandName}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Link to="/login" style={{ color: 'var(--text2)', textDecoration: 'none', fontSize: 14, fontWeight: 600, padding: '8px 6px' }}>Login</Link>
            <Link to="/register" className="btn btn-primary btn-sm">Start Free</Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '56px 20px 48px', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🤖</div>
        <h1 style={{ fontSize: 'clamp(28px, 5vw, 46px)', fontWeight: 800, color: 'var(--text)', lineHeight: 1.25, marginBottom: 6 }}>
          Turn any PDF into a ready exam in minutes
        </h1>
        <h1 style={{ fontSize: 'clamp(22px, 4vw, 34px)', fontWeight: 800, color: 'var(--primary-light)', lineHeight: 1.4, marginBottom: 20 }}>
          যেকোনো PDF থেকে মিনিটেই তৈরি হবে পরীক্ষা
        </h1>
        <p style={{ color: 'var(--text2)', fontSize: 16, maxWidth: 640, margin: '0 auto 4px', lineHeight: 1.8 }}>
          {brandName} uses AI to read your PDF, generate MCQs with two-pass answer verification, and let you publish and share exams instantly.
        </p>
        <p style={{ color: 'var(--text2)', fontSize: 15, maxWidth: 640, margin: '0 auto 28px', lineHeight: 1.9 }}>
          AI আপনার PDF পড়ে, দুই-ধাপে যাচাই করে MCQ তৈরি করে দেয় — আপনি সাথে সাথেই পরীক্ষা পাবলিশ ও শেয়ার করতে পারবেন।
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/register" className="btn btn-primary btn-lg">🚀 Start Free</Link>
        </div>
        {tagline && (
          <p style={{ color: 'var(--text3)', fontSize: 13, marginTop: 24 }}>{tagline}</p>
        )}
      </div>

      {/* Features */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 20px 56px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
          {FEATURES.map((f, i) => (
            <div key={i} className="card" style={{ padding: '22px 24px' }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>{f.icon}</div>
              <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: 15, marginBottom: 2 }}>{f.title}</div>
              <div style={{ fontWeight: 700, color: 'var(--primary-light)', fontSize: 14, marginBottom: 8 }}>{f.titleBn}</div>
              <div style={{ color: 'var(--text2)', fontSize: 13, lineHeight: 1.7, marginBottom: 4 }}>{f.desc}</div>
              <div style={{ color: 'var(--text2)', fontSize: 13, lineHeight: 1.9 }}>{f.descBn}</div>
            </div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <div style={{ background: 'var(--bg2)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 20px' }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)', textAlign: 'center', marginBottom: 2 }}>How it works</h2>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--primary-light)', textAlign: 'center', marginBottom: 32 }}>কীভাবে কাজ করে</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {STEPS.map((s, i) => (
              <div key={i} className="card" style={{ textAlign: 'center', padding: '24px 16px' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, margin: '0 auto 12px' }}>{i + 1}</div>
                <div style={{ fontSize: 28, marginBottom: 10 }}>{s.icon}</div>
                <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: 14, marginBottom: 4 }}>{s.title}</div>
                <div style={{ color: 'var(--text2)', fontSize: 13, lineHeight: 1.7 }}>{s.titleBn}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '56px 20px' }}>
        <div style={{ textAlign: 'center', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 20, padding: '40px 24px' }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', marginBottom: 2 }}>Ready to save hours on exam prep?</h2>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary-light)', marginBottom: 20 }}>পরীক্ষা তৈরিতে সময় বাঁচাতে চান?</h2>
          <Link to="/register" className="btn btn-primary btn-lg">🚀 Start Free</Link>
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ color: 'var(--text3)', fontSize: 13 }}>© {new Date().getFullYear()} {brandName}. All rights reserved.</div>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <Link to="/about" style={{ color: 'var(--text2)', fontSize: 13, textDecoration: 'none' }}>About Us</Link>
            <Link to="/contact" style={{ color: 'var(--text2)', fontSize: 13, textDecoration: 'none' }}>Contact</Link>
            <Link to="/privacy-policy" style={{ color: 'var(--text2)', fontSize: 13, textDecoration: 'none' }}>Privacy Policy</Link>
            <Link to="/terms" style={{ color: 'var(--text2)', fontSize: 13, textDecoration: 'none' }}>Terms</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
